import React, { useEffect, useRef, useState } from 'react';
import { Video } from 'lucide-react';
import { Asset } from '../../hooks/useAssets';
import { getAssetPreviewImageUrl } from '../../lib/assetPreview';

const VISIBILITY_ROOT_MARGIN = '200px';
const FALLBACK_FRAME_SECONDS = 0.25;

interface AssetVideoThumbnailProps {
  asset: Asset;
}

export const AssetVideoThumbnail: React.FC<AssetVideoThumbnailProps> = ({ asset }) => {
  const previewImageUrl = getAssetPreviewImageUrl(asset);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const requestedSeekRef = useRef(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [frameReady, setFrameReady] = useState(false);
  const [frameFailed, setFrameFailed] = useState(false);

  useEffect(() => {
    if (previewImageUrl || frameFailed) return;

    const element = containerRef.current;
    if (!element) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoadVideo(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: VISIBILITY_ROOT_MARGIN },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [frameFailed, previewImageUrl]);

  const renderPlaceholder = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-bg-secondary">
      <Video className="w-8 h-8 text-text-tertiary" />
    </div>
  );

  if (previewImageUrl) {
    return (
      <img
        src={previewImageUrl}
        alt={asset.name}
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <div ref={containerRef} className="relative w-full h-full bg-bg-secondary">
      {shouldLoadVideo && !frameFailed && (
        <video
          src={asset.storageUrl}
          muted
          playsInline
          preload="metadata"
          className={`w-full h-full object-cover ${frameReady ? 'opacity-100' : 'opacity-0'}`}
          onLoadedMetadata={(e) => {
            const video = e.currentTarget;
            const duration = Number.isFinite(video.duration) ? video.duration : 0;
            const maxSeekTime = duration > 0 ? Math.max(duration - 0.01, 0) : 0;
            const nextTime = Math.min(FALLBACK_FRAME_SECONDS, maxSeekTime);

            if (nextTime <= 0) {
              requestedSeekRef.current = false;
              setFrameReady(true);
              return;
            }

            requestedSeekRef.current = true;
            try {
              video.currentTime = nextTime;
            } catch {
              setFrameFailed(true);
            }
          }}
          onLoadedData={() => {
            if (!requestedSeekRef.current) {
              setFrameReady(true);
            }
          }}
          onSeeked={() => {
            setFrameReady(true);
          }}
          onError={() => {
            setFrameFailed(true);
          }}
        />
      )}
      {(!frameReady || frameFailed) && renderPlaceholder()}
    </div>
  );
};
